import { validateOrFail as validateOrFailCnpj } from 'validation-br/dist/cnpj'
import { validateOrFail as validateOrFailCpf } from 'validation-br/dist/cpf'
import * as yup from 'yup'

import { hasUploadValue } from 'utils/customFieldUpload'
import { normalizeDocument } from 'utils/document'
import { IField } from 'utils/GenerateSchema'

export interface ExtraFieldItem {
  uuid: string
  name: string
  required: boolean
  extra_fieldable_type: string | number
  field_type?: string | number
  options?: Array<{ label: string; value: string }>
}

const baseCustomerSchema = {
  foreign: yup.boolean(),
  city: yup.string(),
  complement: yup.string(),
  document: yup
    .string()
    .required('Documento é obrigatório.')
    .when('foreign', {
      is: true,
      then: (schema) => schema,
      otherwise: (schema) =>
        schema.test('cpfCnpj', 'CPF/CNPJ inválido', function (value?: string) {
          const { path, createError } = this
          if (!value) return true

          try {
            value = normalizeDocument(value)
            if (value.length === 11) {
              validateOrFailCpf(value)
            } else {
              validateOrFailCnpj(value)
            }
            return true
          } catch (error) {
            return createError({
              message: 'CPF/CNPJ inválido',
              path
            })
          }
        })
    }),
  email: yup
    .string()
    .nullable()
    .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, {
      message: 'O e-mail informado contém caracteres inválidos.',
      excludeEmptyString: true
    })
    .when('hasContract', {
      is: true,
      then: (schema) =>
        schema.required('Email é obrigatório para gerar o contrato.')
    }),
  name: yup.string().required('Nome do cliente é obrigatório.'),
  neighborhood: yup.string(),
  nickname: yup.string(),
  numberHome: yup.string(),
  phone: yup.string(),
  // Campo opcional de regime tributário do customer
  tributary_regime: yup.string().nullable(),
  // Campo opcional de inscrição municipal do customer
  municipal_registration: yup.string().nullable(),
  postalCode: yup.string(),
  state: yup
    .object({
      label: yup.string(),
      uuid: yup.string()
    })
    .nullable(),
  street: yup.string(),
  create_user: yup.bool(),
  is_lead: yup.bool()
}

const createExtraFieldValidation = (field: ExtraFieldItem) => {
  const fieldName = `extraField_${field.uuid}`
  const fieldType = field.extra_fieldable_type || field.field_type

  let validation: any

  switch (fieldType) {
    case 'text':
    case 'short_text':
    case 'long_text':
    case 0:
      validation = yup.string().nullable()
      break

    case 'radio':
    case 'select':
    case 1:
      validation = yup.string().nullable()
      break

    case 'checkbox':
    case 2:
      validation = yup.array().of(yup.string()).nullable()
      break

    case 'upload':
    case 4:
      validation = yup.mixed().nullable()
      break

    case 'accept':
      validation = yup.boolean().nullable()
      break

    default:
      validation = yup.string().nullable()
      break
  }

  if (field.required) {
    if (fieldType === 'checkbox' || fieldType === 2) {
      validation = validation
        .min(1, `${field.name} é obrigatório.`)
        .required(`${field.name} é obrigatório.`)
    } else if (fieldType === 'accept') {
      validation = validation.oneOf([true], `${field.name} é obrigatório.`)
    } else if (fieldType === 'upload' || fieldType === 4) {
      // uuid do arquivo ou lista de uuids (campo com vários arquivos)
      validation = validation.test(
        'upload-required',
        `${field.name} é obrigatório.`,
        (value: unknown) => hasUploadValue(value)
      )
    } else {
      validation = validation.required(`${field.name} é obrigatório.`)
    }
  } else {
    validation = validation.optional()
  }

  return { [fieldName]: validation }
}

export const createDynamicCustomerSchema = (
  extraFields: ExtraFieldItem[] = []
) => {
  const extraFieldsSchema = extraFields.reduce((acc, field) => {
    return { ...acc, ...createExtraFieldValidation(field) }
  }, {})

  return yup.object({
    ...baseCustomerSchema,
    ...extraFieldsSchema,
    // Internal identifiers validation
    hasIdentifyInternal: yup.boolean(),
    internalIdentifiers: yup
      .array()
      .when('hasIdentifyInternal', {
        is: true,
        then: (schema) =>
          schema
            .of(
              yup.object().shape({
                name: yup
                  .string()
                  .required('Preencha o nome do identificador interno.'),
                value: yup
                  .string()
                  .required('Preencha o valor do identificador interno.')
              })
            )
            .min(1, 'Adicione pelo menos um identificador interno.'),
        otherwise: (schema) => schema
      })
      .default([]),
    hasContract: yup.boolean().default(false),
    contractModelUuid: yup.string().nullable().optional(),
    contractEndDate: yup.string().nullable().optional()
  })
}

export const schemaCustomer = yup.object(baseCustomerSchema)

export const FormFieldsCustomer: IField[] = [
  {
    name: 'city',
    type: yup.string(),
    standartForm: true
  },
  {
    name: 'complement',
    type: yup.string(),
    standartForm: true
  },
  {
    name: 'document',
    type: yup
      .string()
      .required('CPF/CNPJ é obrigatório.')
      .test('cpfCnpj', 'CPF/CNPJ inválido', function (value?: string) {
        const { path, createError } = this
        if (!value) return true

        try {
          value = value.replace(/\D/g, '')
          if (value.length === 11) {
            validateOrFailCpf(value)
          } else {
            validateOrFailCnpj(value)
          }
          return true
        } catch (error) {
          return createError({
            message: 'CPF/CNPJ inválido',
            path
          })
        }
      }),
    standartForm: true
  },
  {
    name: 'email',
    type: yup
      .string()
      .matches(
        /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'O e-mail informado contém caracteres inválidos.'
      ),
    standartForm: true
  },
  {
    name: 'name',
    type: yup.string().required('Nome do cliente é obrigatório.'),
    standartForm: true
  },
  {
    name: 'neighborhood',
    type: yup.string(),
    standartForm: true
  },
  {
    name: 'nickname',
    type: yup.string(),
    standartForm: true
  },
  {
    name: 'numberHome',
    type: yup.string(),
    standartForm: true
  },
  {
    name: 'phone',
    type: yup.string(),
    standartForm: true
  },
  {
    name: 'postalCode',
    type: yup.string(),
    standartForm: true
  },
  {
    name: 'state',
    type: yup.object({
      label: yup.string(),
      uuid: yup.string()
    }),
    standartForm: true
  },
  {
    name: 'street',
    type: yup.string(),
    standartForm: true
  },
  {
    name: 'create_user',
    type: yup.bool(),
    standartForm: true
  }
]
